import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Upload, Inbox, Search, SlidersHorizontal, CheckCircle2, Camera, Crop, Paperclip, File,
  FileText, PenLine, MessageCircle, Mic, Download, Eye, ThumbsUp, MoreVertical, ChevronDown,
  ArrowRight, Lightbulb, Star, Plus, Send, Loader2, Image as ImageIcon,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import SmartIntake from "./SmartIntake";
import { Card } from "./ui";
import { Spinner } from "@/components/common";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const APPROVABLE = ["vorschlag", "warten_auf_freigabe", "rueckfrage_offen", "zusatz_vorgeschlagen"];
const STATUS_TONE = { eingegangen: "#60a5fa", neu: "#94a3b8", in_pruefung: "#f59e0b", umgewandelt: "#22c55e", erledigt: "#22c55e", abgelehnt: "#f87171" };
const EFFORT_DOT = { klein: "#22c55e", mittel: "#f59e0b", gross: "#f87171" };
const KIND_ICON = { foto: Camera, screenshot: Crop, datei: Paperclip, wunsch: PenLine, frage: MessageCircle, freigabe: ThumbsUp, sprachnachricht: Mic };

const fmtSize = (b) => { b = Number(b || 0); if (!b) return ""; if (b < 1024) return `${b} B`; if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`; if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`; return `${(b / 1073741824).toFixed(2)} GB`; };
const fmtGB = (b) => `${(Number(b || 0) / 1073741824).toFixed(2)} GB`;
const fmtFromMime = (m = "") => { const map = { "image/jpeg": "JPG", "image/png": "PNG", "image/webp": "WEBP", "application/pdf": "PDF" }; if (map[m]) return map[m]; const p = (m || "").split("/")[1]; return p ? p.toUpperCase() : ""; };
const isImg = (m = "", u = "") => (m || "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(u || "");
const fileUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND}${u}`);
const initials = (n = "") => (n.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("") || "?").toUpperCase();
const rel = (s, t) => {
  if (!s) return "";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (diff < 90) return t("gerade eben");
  if (diff < 3600) return `${t("vor")} ${Math.floor(diff / 60)} ${t("Min.")}`;
  if (diff < 86400) { const h = Math.floor(diff / 3600); return `${t("vor")} ${h} ${h === 1 ? t("Stunde") : t("Stunden")}`; }
  const d = Math.floor(diff / 86400); return `${t("vor")} ${d} ${d === 1 ? t("Tag") : t("Tagen")}`;
};

export default function CFiles() {
  const { overview, accent, clientId, base, openAi, startTour } = useCockpit();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isStaff = ["super_admin", "team_member"].includes(user.role);
  const canDoc = isStaff || user.role === "client" || (user.allowed_sections || []).includes("documents");
  const company = overview?.client?.company_name || "";
  const userName = user?.name || user?.email || "";

  const [reqs, setReqs] = useState(null);
  const [docs, setDocs] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [storage, setStorage] = useState(null);
  const [tab, setTab] = useState(loc.pathname.endsWith("/freigaben") ? "freigaben" : "neu");
  const [limit, setLimit] = useState(6);
  const [q, setQ] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const actionsRef = useRef({});
  const cidQ = clientId ? `?client_id=${clientId}` : "";
  const cidQ2 = clientId ? `&client_id=${clientId}` : "";

  const loadReqs = () => api.get(`/cockpit/ai/requests${cidQ}`).then((r) => setReqs((r.data.requests || []).filter((x) => x.source === "client_intake"))).catch(() => setReqs([]));
  const loadDocs = () => api.get(`/cockpit/documents?lang=${lang}${cidQ2}`).then((r) => setDocs(r.data || [])).catch(() => setDocs([]));
  const loadIdeas = () => api.get(`/cockpit/design-ideas${cidQ}`).then((r) => setIdeas(r.data.ideas || [])).catch(() => setIdeas([]));
  const loadStorage = () => api.get(`/cockpit/files/storage${cidQ}`).then((r) => setStorage(r.data)).catch(() => {});
  useEffect(() => { loadReqs(); loadDocs(); loadIdeas(); loadStorage(); /* eslint-disable-next-line */ }, [clientId, lang]);
  useEffect(() => { setLimit(6); }, [tab, q]);
  useEffect(() => { setTab(loc.pathname.endsWith("/freigaben") ? "freigaben" : "neu"); }, [loc.pathname]);

  const refreshAll = () => { loadReqs(); loadDocs(); loadStorage(); };

  const approve = async (id) => {
    setBusyId(id);
    try { await api.post(`/cockpit/design-ideas/${id}/approve${cidQ}`); toast.success(t("Zur Umsetzung freigegeben")); loadIdeas(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Senden fehlgeschlagen")); }
    finally { setBusyId(null); }
  };
  const requestExtra = async (tier) => {
    try { await api.post(`/cockpit/files/storage/request-extra${cidQ}`, tier ? { tier } : {}); toast.success(t("Anfrage gesendet — KEWAG meldet sich zu Ihrem Speicher.")); }
    catch (e) { toast.error(e.response?.data?.detail || t("Senden fehlgeschlagen")); }
  };
  const download = async (d) => {
    setBusyId(d.id);
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.file_name || "datei";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error(t("Download fehlgeschlagen")); } finally { setBusyId(null); }
  };

  const approvable = useMemo(() => (ideas || []).filter((i) => i.needs_client_approval && APPROVABLE.includes(i.status)), [ideas]);
  const rq = reqs || [];
  const by = (arr) => rq.filter((x) => arr.includes(x.status));
  const counts = {
    neu: by(["eingegangen"]).length, pruefung: by(["neu", "in_pruefung"]).length, freigaben: approvable.length,
    umsetzung: by(["umgewandelt"]).length, erledigt: by(["erledigt", "abgelehnt"]).length, dateien: (docs || []).length,
  };

  const TABS = [
    { key: "neu", label: t("Neue Eingänge") },
    { key: "pruefung", label: t("In Prüfung") },
    { key: "freigaben", label: t("Freigaben offen") },
    { key: "umsetzung", label: t("In Umsetzung") },
    { key: "erledigt", label: t("Erledigt") },
    { key: "dateien", label: t("Alle Dateien") },
  ];

  if (reqs === null || docs === null) return <Spinner />;

  const term = q.trim().toLowerCase();
  const matchReq = (r) => !term || `${r.card?.subject || ""} ${r.card?.summary || ""} ${r.text || ""}`.toLowerCase().includes(term);
  const matchIdea = (i) => !term || `${i.title} ${i.description} ${i.expected_benefit} ${t(i.category_label)}`.toLowerCase().includes(term);
  const matchDoc = (d) => !term || `${d.file_name} ${t(d.category_label || "")}`.toLowerCase().includes(term);

  let list = [];
  let variant = "intake";
  if (tab === "neu") list = by(["eingegangen"]).filter(matchReq);
  else if (tab === "pruefung") list = by(["neu", "in_pruefung"]).filter(matchReq);
  else if (tab === "umsetzung") list = by(["umgewandelt"]).filter(matchReq);
  else if (tab === "erledigt") list = by(["erledigt", "abgelehnt"]).filter(matchReq);
  else if (tab === "freigaben") { list = approvable.filter(matchIdea); variant = "approval"; }
  else if (tab === "dateien") { list = (docs || []).filter(matchDoc); variant = "doc"; }
  const shown = list.slice(0, limit);

  return (
    <div className="fade-in" data-testid="cockpit-files">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3.5">
          <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border" style={{ background: `${accent}14`, color: accent, borderColor: `${accent}44` }}>
            <Upload className="w-6 h-6" />
          </span>
          <div>
            <h1 className="text-[26px] lg:text-[34px] leading-none font-black tracking-tight">{t("Dateien & Freigaben")}</h1>
            <p className="text-sm text-gray-400 mt-2">{t("Senden, teilen, freigeben. KEWAG macht den Rest.")}</p>
          </div>
        </div>
        <button onClick={() => actionsRef.current.datei?.()} data-testid="cf-neu-senden"
          className="hidden sm:inline-flex items-center gap-2 text-sm font-bold rounded-xl px-4 py-2.5 text-black active:scale-95 transition-transform shrink-0" style={{ background: accent }}>
          <Plus className="w-4 h-4" /> {t("Neu senden")}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_330px] gap-5 items-start">
        {/* MAIN */}
        <div className="space-y-5 min-w-0">
          <div id="cf-send">
            {canDoc && <SmartIntake accent={accent} clientId={clientId} onDone={refreshAll} onCheckApprovals={() => setTab("freigaben")} onReady={(a) => { actionsRef.current = a; }} />}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 flex-wrap border-b border-white/[0.06]" data-testid="cf-tabs">
            <div className="flex items-center gap-1 flex-wrap -mb-px flex-1 min-w-0">
              {TABS.map((tb) => {
                const on = tb.key === tab;
                const c = counts[tb.key];
                return (
                  <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`cf-tab-${tb.key}`}
                    className={`relative flex items-center gap-2 px-3.5 py-3 text-[13px] font-semibold whitespace-nowrap transition-colors ${on ? "text-white" : "text-gray-400 hover:text-gray-200"}`}>
                    {tb.label}
                    {c > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold"
                        style={on ? { background: accent, color: "#0A0A0C" } : { background: "rgba(255,255,255,0.1)", color: "#d1d5db" }}>{c}</span>
                    )}
                    {on && <span className="absolute bottom-0 inset-x-2 h-[2.5px] rounded-full" style={{ background: accent }} />}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setShowSearch((s) => !s)} data-testid="cf-filter-toggle"
              className={`inline-flex items-center gap-2 text-[13px] font-semibold rounded-xl px-3.5 py-2 border transition-colors mb-2 ${showSearch ? "text-black" : "border-white/10 text-gray-300 hover:bg-white/5"}`}
              style={showSearch ? { background: accent, borderColor: accent } : {}}>
              <SlidersHorizontal className="w-4 h-4" /> {t("Filter & Suche")}
            </button>
          </div>

          {showSearch && (
            <div className="relative -mt-2" data-testid="cf-search">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Suchen in Dateien, Wünschen, Freigaben …")}
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-gray-200 focus:outline-none focus:border-[color:var(--client-accent)]/60" />
            </div>
          )}

          {/* Liste */}
          <Card className="p-0 overflow-hidden" data-testid="cf-list">
            {shown.length === 0 ? (
              <EmptyState tab={tab} t={t} />
            ) : (
              <>
                {shown.map((it) =>
                  variant === "approval" ? <ApprovalRow key={it.id} i={it} accent={accent} t={t} busy={busyId === it.id} onApprove={() => approve(it.id)} onDetails={() => nav(`${base}/design`)} onQuestion={() => openAi()} userName={userName} company={company} />
                  : variant === "doc" ? <DocRow key={it.id} d={it} accent={accent} t={t} busy={busyId === it.id} onDownload={() => download(it)} userName={userName} company={company} />
                  : <IntakeRow key={it.id} r={it} accent={accent} t={t} onQuestion={() => openAi()} userName={userName} company={company} />
                )}
                {list.length > limit && (
                  <button onClick={() => setLimit((l) => l + 6)} data-testid="cf-more"
                    className="w-full flex items-center justify-center gap-1.5 py-3.5 text-[13px] font-semibold text-gray-400 hover:text-white hover:bg-white/[0.03] transition-colors border-t border-white/[0.05]">
                    {t("Weitere anzeigen")} <ChevronDown className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </Card>
        </div>

        {/* RIGHT SIDEBAR */}
        <aside className="space-y-4 xl:sticky xl:top-4" data-testid="cf-sidebar">
          <WasIstDas t={t} accent={accent} />
          <Schnellaktionen t={t} accent={accent} actionsRef={actionsRef} />
          {storage && <Speicherplatz t={t} accent={accent} storage={storage} onRequest={requestExtra} />}
          <TippCard t={t} accent={accent} onMore={() => startTour?.()} />
        </aside>
      </div>
    </div>
  );
}

/* ---------- Rows ---------- */
function RowRight({ tone, statusLabel, time, userName, company, children }) {
  return (
    <div className="flex items-center gap-4 lg:gap-5 flex-wrap lg:flex-nowrap lg:justify-end lg:shrink-0">
      <div className="min-w-[120px]">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-200"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: tone }} /> {statusLabel}</div>
        <div className="text-[11px] text-gray-500 mt-0.5 pl-4">{time}</div>
      </div>
      <div className="flex items-center gap-2 min-w-[130px]">
        <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-black shrink-0" style={{ background: "#A3E635" }}>{initials(userName)}</span>
        <div className="leading-tight min-w-0"><div className="text-[12px] font-semibold text-gray-200 truncate">{userName}</div><div className="text-[11px] text-gray-500 truncate">{company}</div></div>
      </div>
      <div className="flex flex-col gap-2 shrink-0">{children}</div>
    </div>
  );
}
const OutBtn = ({ onClick, icon: Icon, children, testid, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className="inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold rounded-xl px-3.5 py-2 border border-white/10 text-gray-200 hover:bg-white/[0.06] disabled:opacity-50 transition-colors whitespace-nowrap">
    {Icon && <Icon className="w-3.5 h-3.5" />}{children}
  </button>
);
const GreenBtn = ({ onClick, icon: Icon, children, testid, accent, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className="inline-flex items-center justify-center gap-1.5 text-[12px] font-bold rounded-xl px-3.5 py-2 text-black disabled:opacity-50 active:scale-95 transition-transform whitespace-nowrap" style={{ background: accent }}>
    {disabled ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : Icon && <Icon className="w-3.5 h-3.5" />}{children}
  </button>
);

function RowWrap({ children }) {
  return <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-4 border-b border-white/[0.05] last:border-0" data-testid="cf-row">{children}</div>;
}

function Thumb({ children, badge }) {
  return (
    <div className="relative w-full lg:w-[104px] h-24 lg:h-[76px] rounded-xl overflow-hidden border border-white/10 bg-white/[0.03] flex items-center justify-center shrink-0">
      {badge && <span className="absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-md text-black" style={{ background: "#A3E635" }}>{badge}</span>}
      {children}
    </div>
  );
}

function IntakeRow({ r, accent, t, onQuestion, userName, company }) {
  const Icon = KIND_ICON[r.intake_kind] || Paperclip;
  const tone = STATUS_TONE[r.status] || "#94a3b8";
  const img = (r.attachments || []).find((a) => isImg(a.content_type, a.url));
  const kindLabel = { foto: t("Foto"), screenshot: t("Screenshot"), datei: t("Datei"), wunsch: t("Wunsch"), frage: t("Frage"), freigabe: t("Freigabe"), sprachnachricht: t("Sprachnachricht") }[r.intake_kind] || t("Eingang");
  const fmt = img ? fmtFromMime(img.content_type) : "";
  const meta = [kindLabel, fmt].filter(Boolean).join(" • ");
  return (
    <RowWrap>
      <Thumb badge={r.status === "eingegangen" ? t("NEU") : null}>
        {img ? <img src={fileUrl(img.url)} alt="" className="w-full h-full object-cover" /> : <Icon className="w-7 h-7" style={{ color: accent }} />}
      </Thumb>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[15px] text-gray-100">{r.card?.subject || kindLabel}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">{meta}</div>
        {(r.card?.summary || r.text) && <p className="text-[13px] text-gray-300 mt-1.5 line-clamp-2 break-words">{r.card?.summary || r.text}</p>}
        <div className="text-[11px] text-gray-600 mt-1.5">{r.request_no}</div>
      </div>
      <RowRight tone={tone} statusLabel={r.status_label} time={rel(r.created_at, t)} userName={userName} company={company}>
        <OutBtn onClick={onQuestion} icon={MessageCircle} testid={`cf-question-${r.id}`}>{t("Frage stellen")}</OutBtn>
      </RowRight>
    </RowWrap>
  );
}

function ApprovalRow({ i, accent, t, busy, onApprove, onDetails, onQuestion, userName, company }) {
  return (
    <RowWrap>
      <Thumb>
        {i.preview_image_url ? <img src={i.preview_image_url} alt="" className="w-full h-full object-cover" /> : <Lightbulb className="w-7 h-7" style={{ color: accent }} />}
      </Thumb>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[15px] text-gray-100">{i.title}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">{t(i.category_label)}</div>
        {(i.description || i.expected_benefit) && <p className="text-[13px] text-gray-300 mt-1.5 line-clamp-2 break-words">{i.description || i.expected_benefit}</p>}
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          {i.effort_label && <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-300"><span className="w-2 h-2 rounded-full" style={{ background: EFFORT_DOT[i.effort] || "#9ca3af" }} /> {t("Aufwand")}: {t(i.effort_label)}</span>}
          {i.included_in_abo && <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-300"><span className="w-2 h-2 rounded-full bg-emerald-400" /> {t("Inkl. im Abo")}</span>}
          {i.price_display && !i.included_in_abo && <span className="text-[11px] font-semibold text-gray-400">{i.price_display}</span>}
        </div>
      </div>
      <RowRight tone="#f59e0b" statusLabel={i.status_label} time={rel(i.updated_at || i.created_at, t)} userName={userName} company={company}>
        <GreenBtn onClick={onApprove} icon={ThumbsUp} accent={accent} disabled={busy} testid={`cf-approve-${i.id}`}>{t("Jetzt freigeben")}</GreenBtn>
        <OutBtn onClick={onQuestion} icon={MessageCircle} testid={`cf-approval-question-${i.id}`}>{t("Kommentar")}</OutBtn>
      </RowRight>
    </RowWrap>
  );
}

function DocRow({ d, accent, t, busy, onDownload, userName, company }) {
  const img = isImg(d.mime_type, d.storage_path || d.file_name);
  const isPdf = (d.mime_type || "").includes("pdf") || /\.pdf$/i.test(d.file_name || "");
  const meta = [fmtFromMime(d.mime_type) || (isPdf ? "PDF" : ""), fmtSize(d.file_size)].filter(Boolean).join(" • ");
  const openUrl = `${API}/documents/${d.id}/download`;
  return (
    <RowWrap>
      <Thumb>
        {img ? <img src={openUrl} alt="" className="w-full h-full object-cover" />
          : isPdf ? <span className="flex flex-col items-center justify-center text-red-400"><FileText className="w-7 h-7" /><span className="text-[9px] font-bold mt-0.5">PDF</span></span>
          : <File className="w-7 h-7 text-gray-500" />}
      </Thumb>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[15px] text-gray-100 truncate" title={d.file_name}>{d.file_name}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">{meta}</div>
        <div className="text-[11px] text-gray-500 mt-1.5">{t(d.category_label || "")}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}</div>
      </div>
      <RowRight tone="#94a3b8" statusLabel={t(d.category_label || t("Datei"))} time={rel(d.created_at, t)} userName={userName} company={company}>
        <OutBtn onClick={() => window.open(openUrl, "_blank")} icon={Eye} testid={`cf-doc-view-${d.id}`}>{t("Öffnen")}</OutBtn>
        <GreenBtn onClick={onDownload} icon={Download} accent={accent} disabled={busy} testid={`cf-doc-download-${d.id}`}>{t("Herunterladen")}</GreenBtn>
      </RowRight>
    </RowWrap>
  );
}

function EmptyState({ tab, t }) {
  const map = {
    neu: { icon: Inbox, title: t("Noch keine neuen Eingänge."), hint: t("Senden Sie oben ein Foto, eine Datei oder Ihren Wunsch — es erscheint dann sofort hier.") },
    pruefung: { icon: Search, title: t("Aktuell nichts in Prüfung."), hint: t("Sobald KEWAG Ihre Eingaben prüft, sehen Sie es hier.") },
    freigaben: { icon: ThumbsUp, title: t("Keine offenen Freigaben."), hint: t("Wenn KEWAG einen Vorschlag zur Freigabe bereitstellt, erscheint er hier.") },
    umsetzung: { icon: CheckCircle2, title: t("Aktuell nichts in Umsetzung."), hint: t("Freigegebene Anliegen erscheinen hier, solange wir daran arbeiten.") },
    erledigt: { icon: CheckCircle2, title: t("Noch nichts erledigt."), hint: t("Abgeschlossene Anliegen sammeln sich hier.") },
    dateien: { icon: Paperclip, title: t("Noch keine Dateien vorhanden."), hint: t("Sobald Sie etwas senden oder KEWAG etwas bereitstellt, erscheint es hier.") },
  };
  const e = map[tab] || map.neu;
  const Icon = e.icon;
  return (
    <div className="flex flex-col items-center text-center py-14 px-6" data-testid="cf-empty">
      <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3.5 bg-white/[0.04] text-gray-600"><Icon className="w-7 h-7" /></span>
      <div className="text-[15px] font-semibold text-gray-200">{e.title}</div>
      <p className="text-[13px] text-gray-500 mt-1.5 max-w-sm">{e.hint}</p>
    </div>
  );
}

/* ---------- Right sidebar ---------- */
function WasIstDas({ t, accent }) {
  const bullets = [t("Einfach senden"), t("Wir ordnen ein"), t("Schnellere Umsetzung"), t("Sie sehen das Ergebnis")];
  return (
    <Card className="p-5" data-testid="cf-was-ist-das">
      <div className="flex items-center gap-2 mb-2.5"><Lightbulb className="w-4.5 h-4.5" style={{ color: accent }} /><h3 className="font-bold text-[15px]">{t("Was ist das?")}</h3></div>
      <p className="text-[13px] text-gray-400 leading-relaxed">{t("Hier senden Sie Dateien, Wünsche und Freigaben direkt an KEWAG. Wir ordnen alles ein, setzen es um und zeigen Ihnen jederzeit den aktuellen Stand.")}</p>
      <div className="mt-3.5 space-y-2">
        {bullets.map((b) => <div key={b} className="flex items-center gap-2 text-[13px] text-gray-200"><CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: accent }} /> {b}</div>)}
      </div>
    </Card>
  );
}

function Schnellaktionen({ t, accent, actionsRef }) {
  const items = [
    { key: "foto", icon: Camera, label: t("Foto senden") },
    { key: "screenshot", icon: Crop, label: t("Screenshot senden") },
    { key: "datei", icon: Upload, label: t("Datei hochladen") },
    { key: "wunsch", icon: PenLine, label: t("Wunsch schreiben") },
    { key: "freigabe", icon: CheckCircle2, label: t("Freigabe prüfen") },
    { key: "frage", icon: MessageCircle, label: t("Frage an KEWAG stellen") },
  ];
  return (
    <Card className="p-5" data-testid="cf-schnellaktionen">
      <h3 className="font-bold text-[15px] mb-3">{t("Schnellaktionen")}</h3>
      <div className="space-y-1">
        {items.map((it) => (
          <button key={it.key} onClick={() => actionsRef.current?.[it.key]?.()} data-testid={`cf-quick-${it.key}`}
            className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl hover:bg-white/[0.04] transition-colors text-left group">
            <it.icon className="w-4 h-4 text-gray-400 group-hover:text-white shrink-0" />
            <span className="text-[13px] text-gray-200 flex-1">{it.label}</span>
            <MoreVertical className="w-4 h-4 text-gray-600" />
          </button>
        ))}
      </div>
    </Card>
  );
}

function Speicherplatz({ t, accent, storage, onRequest }) {
  const pct = Math.min(storage.used_pct || 0, 100);
  const bar = pct >= 100 ? "#ef4444" : pct >= 80 ? "#f59e0b" : accent;
  const tiers = storage.upgrade_tiers || [];
  return (
    <Card className="p-5" data-testid="cf-speicher">
      <h3 className="font-bold text-[15px] mb-3">{t("Speicherplatz")}</h3>
      <div className="text-[13px] text-gray-400">{fmtGB(storage.quota_bytes)} {t("inklusive")}</div>
      <div className="h-2.5 rounded-full bg-white/[0.06] overflow-hidden my-2.5" data-testid="cf-speicher-bar"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: bar }} /></div>
      <div className="text-[12px] text-gray-400">{fmtGB(storage.used_bytes)} {t("verwendet")} ({pct.toFixed(0)}%)</div>
      {tiers.length > 0 && (
        <div className="mt-3.5 space-y-1.5">
          {tiers.map((tr) => (
            <button key={tr.label} onClick={() => onRequest(`${tr.label} · CHF ${tr.price_chf}.- / ${tr.period}`)} data-testid={`cf-tier-${tr.add_gb}`}
              className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-xl hover:bg-white/[0.04] transition-colors text-left">
              <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-200"><Plus className="w-3.5 h-3.5" style={{ color: accent }} /> {tr.label}</span>
              <span className="text-[12px] text-gray-500">CHF {tr.price_chf}.- / {t(tr.period)}</span>
            </button>
          ))}
        </div>
      )}
      <button onClick={() => onRequest("")} data-testid="cf-speicher-erweitern"
        className="w-full mt-3.5 inline-flex items-center justify-center gap-2 text-[13px] font-bold rounded-xl py-2.5 text-black active:scale-95 transition-transform" style={{ background: accent }}>
        <Upload className="w-4 h-4" /> {t("Speicher erweitern")}
      </button>
    </Card>
  );
}

function TippCard({ t, accent, onMore }) {
  return (
    <Card className="p-5" data-testid="cf-tipp">
      <div className="flex items-center gap-2 mb-2.5"><Star className="w-4.5 h-4.5" style={{ color: accent }} /><h3 className="font-bold text-[15px]">{t("Tipp")}</h3></div>
      <p className="text-[13px] text-gray-400 leading-relaxed">{t("Je genauer Sie Ihr Anliegen beschreiben und je besser das Bild oder der Screenshot, desto schneller können wir es für Sie umsetzen.")}</p>
      <button onClick={onMore} data-testid="cf-tipp-more" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold hover:gap-2.5 transition-all" style={{ color: accent }}>
        {t("Mehr Tipps ansehen")} <ArrowRight className="w-4 h-4" />
      </button>
    </Card>
  );
}
